/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useMemo, useState } from 'react';
import Media from 'react-media';

import classNames from 'classnames';
import type Maybe from 'graphql/tsutils/Maybe';

import Imgix from 'js/components/Imgix';

import { Typography2, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import type { ImageWithMetadataFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import {
  getPartnerCTAData as getHardcodedPartnerCTAData,
  partnerLogoHeights,
} from 'bundles/front-page/constants/defaults';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import useUserAgent from 'bundles/page/hooks/useUserAgent';

const styles: Record<string, Interpolation<Theme>> = {
  partnerLogosRoot: (theme: Theme) => css`
    text-align: center;
    width: 100%;
    padding-top: ${theme.spacing(64)};
    padding-bottom: ${theme.spacing(64)};
    margin: 0 auto;
    max-width: '100%';
    min-height: 299px;
    ${theme.breakpoints.down('sm')} {
      min-height: 358px;
    }
  `,
  title: (theme: Theme) => css`
    line-height: calc(var(--cds-line-height-175) + var(--cds-line-height-200));
    text-align: center;
    margin-bottom: ${theme.spacing(48)};
    ${theme.breakpoints.down('sm')} {
      font-weight: 400;
    }
    ${theme.breakpoints.down('xs')} {
      padding: 0 18px;
    }
  `,
  logos: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 auto;
    max-width: 1132px;
    padding: 0 10px;
    width: 100%;

    @media screen and (max-width: 780px) {
      justify-content: space-around;
      flex-wrap: wrap;

      /* set this way to make images (set by const) in flexbox layout look good */
      max-width: 555px;
    }

    @media screen and (max-width: 525px) {
      max-width: 375px;
      padding-bottom: 15px;
    }
  `,
  partnerImageLink: css`
    display: inline-block;
    margin: 0 10px 30px;

    .partner-logo-image {
      width: 100%;

      @media screen and (max-width: 780px) {
        width: auto;
      }
    }
  `,
  adjustImgHeightTo27LargeScreen: css`
    .partner-logo-image {
      @media screen and (max-width: 780px) {
        height: 27px;
      }
    }
  `,
  adjustImgHeightTo27MdScreen: css`
    .partner-logo-image {
      @media screen and (max-width: 525px) {
        height: 27px;
      }
    }
  `,
  adjustImgHeightTo22MdScreen: css`
    .partner-logo-image {
      @media screen and (max-width: 525px) {
        height: 22px;
      }
    }
  `,
  adjustImgHeightTo17MdScreen: css`
    .partner-logo-image {
      @media screen and (max-width: 525px) {
        height: 17px;
      }
    }
  `,
  seeAllLink: (theme: Theme) =>
    css({
      display: 'inline-block',
      fontSize: '28px',
      '&&': { color: `${theme.palette.black[500]} !important` },

      '&:hover': {
        textDecoration: 'none',
      },

      '.focus-point': {
        color: theme.palette.blue[600],
      },

      '.underline': {
        textDecoration: 'underline',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    }),

  showBackground: css`
    background-color: #f5f5f5;
  `,
  showExtraSpacing: (theme: Theme) => css`
    margin-top: ${theme.spacing(64)};
    ${theme.breakpoints.down('sm')} {
      margin-top: ${theme.spacing(48)};
    }
  `,
  mobileLogos: css`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    margin: 0 var(--cds-spacing-200);
  `,
  mobilePartnerLogosRoot: css`
    text-align: center;
    width: 100%;
    padding: var(--cds-spacing-300) 0 var(--cds-spacing-400);
    margin: 0 auto;
    max-width: '100%';
    min-height: 299px;
  `,
  mobilePartnerImageLink: css`
    flex: 0 25%;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 82px;
    height: 52px;
    margin-bottom: var(--cds-spacing-200);
  `,
  mobileTitle: css`
    line-height: calc(var(--cds-line-height-175) + var(--cds-line-height-200));
    text-align: center;
    margin: var(--cds-spacing-200) var(--cds-spacing-300) var(--cds-spacing-400);
    font-weight: var(--cds-font-weight-600);
  `,
  mobileSeeAllLink: css`
    font-size: var(--cds-font-size-250);
  `,
};

type CTAdata = {
  href: string;
  trackingName: string;
  data: { value: string };
  message: string;
  matches: boolean;
};

export const getStrongRenderer = (className: string) => (props: { children: React.ReactNode; className?: string }) => {
  const { children } = props;
  return <span className={className}>{children}</span>;
};

const CTALink: React.FunctionComponent<CTAdata> = (props) => {
  const [isBeingHovered, setIsBeingHovered] = useState(false);
  const { href, trackingName, data, message, matches } = props;
  const taglineClasses = classNames('focus-point', { underline: isBeingHovered });
  const strongRenderer = useMemo(() => getStrongRenderer(taglineClasses), [taglineClasses]);

  return (
    <div>
      <TrackedA
        trackingName={trackingName}
        withVisibilityTracking={false}
        data={data}
        css={[styles.seeAllLink, matches ? styles.mobileSeeAllLink : '']}
        href={href}
        onMouseOver={() => setIsBeingHovered(true)}
        onFocus={() => setIsBeingHovered(true)}
        onMouseLeave={() => setIsBeingHovered(false)}
      >
        <Markdown
          disallowedTypes={['paragraph']}
          unwrapDisallowed
          renderers={{
            strong: strongRenderer,
          }}
          source={message}
        />
      </TrackedA>
    </div>
  );
};

type Props = {
  partnerLogos: ImageWithMetadataFieldsFragment[];
  partnerLogosTitle: Maybe<string>;
  showBackground?: boolean;
  showExtraSpacing?: boolean;
};

const renderPartnerLogoImages = (partnerLogos: Props['partnerLogos'], matches: boolean) => {
  return partnerLogos.map(({ relationSlug, image, imageAltText }) => {
    const url = image?.url;

    if (!url || !relationSlug) {
      return null;
    }

    const logoHeight = partnerLogoHeights[relationSlug] ?? partnerLogoHeights.default;

    const isGoogleIllinoisOrDuke = relationSlug === 'google' || relationSlug === 'illinois' || relationSlug === 'duke';
    const isImperialOrPenn = relationSlug === 'imperial' || relationSlug === 'penn';
    const isIbmOrStanford = relationSlug === 'ibm' || relationSlug === 'stanford';
    const isUMich = relationSlug === 'umich';

    return (
      <a
        css={
          matches
            ? styles.mobilePartnerImageLink
            : [
                styles.partnerImageLink,
                isGoogleIllinoisOrDuke && styles.adjustImgHeightTo27LargeScreen,
                isUMich && styles.adjustImgHeightTo27MdScreen,
                (isImperialOrPenn || isGoogleIllinoisOrDuke) && styles.adjustImgHeightTo22MdScreen,
                isIbmOrStanford && styles.adjustImgHeightTo17MdScreen,
              ]
        }
        href={`/partners/${relationSlug}`}
        data-id={relationSlug}
        key={relationSlug}
      >
        <Imgix
          className="partner-logo-image"
          alt={imageAltText ?? ''}
          src={url}
          style={matches ? { maxHeight: '75%', maxWidth: '75%' } : {}}
          maxHeight={logoHeight}
        />
      </a>
    );
  });
};

const PartnerLogosCMS: React.FunctionComponent<Props> = ({
  showBackground,
  showExtraSpacing,
  partnerLogos,
  partnerLogosTitle,
}) => {
  const userAgent = useUserAgent();
  const partnerCtaData = getHardcodedPartnerCTAData();
  return (
    <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
      {(matches) => (
        <section
          className="rc-PartnerLogosRedesign"
          css={
            matches
              ? [styles.mobilePartnerLogosRoot, showBackground ? styles.showBackground : '']
              : [
                  styles.partnerLogosRoot,
                  showBackground ? styles.showBackground : '',
                  showExtraSpacing ? styles.showExtraSpacing : '',
                ]
          }
        >
          {partnerLogosTitle && (
            <Typography2
              variant={matches ? 'subtitleLarge' : 'titleSmall'}
              component="h2"
              css={matches ? styles.mobileTitle : styles.title}
            >
              <CTALink {...partnerCtaData} message={partnerLogosTitle} matches={matches} />
            </Typography2>
          )}

          <div css={matches ? styles.mobileLogos : styles.logos}>{renderPartnerLogoImages(partnerLogos, matches)}</div>
        </section>
      )}
    </Media>
  );
};

export default PartnerLogosCMS;
